import {Component, inject, OnInit, signal, Signal} from '@angular/core';
import {IconButtonComponent} from '../../../../shared/components/icon-button/icon-button.component';
import {TranslocoPipe} from '@jsverse/transloco';
import {MatFormFieldModule} from '@angular/material/form-field';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {catchError, debounceTime, delay, distinctUntilChanged, map, of, startWith, switchMap, tap} from 'rxjs';
import {PatientDto} from '../../../patient-management/dtos/patient.dto';
import {toSignal} from '@angular/core/rxjs-interop';
import {CreatePatientDialog} from '../../dialogs/create-patient.dialog/create-patient.dialog';
import {AppointmentSelectionService} from '../../appointment-selection.service';
import {MemoryService} from '../../../../core/services/memory.service';
import {StorageService} from '../../../../core/services/storage.service';
import {MatDialog} from '@angular/material/dialog';
import {LoadingService} from '../../../../core/services/loading.service';
import {PatientSearchDto} from '../../dtos/patient-search.dto';

// Shorter searches would match most of the patients of a practice, so they are answered from the
// current selection instead of going to the backend.
const minSearchTextLength = 2;

@Component({
  selector: 'app-patient-search',
  imports: [
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    TranslocoPipe,
    IconButtonComponent
  ],
  templateUrl: './patient-search.component.html',
  styleUrl: './patient-search.component.scss'
})
export class PatientSearchComponent implements OnInit {
  appointmentSelectionService = inject(AppointmentSelectionService);
  memoryService = inject(MemoryService);
  storageService = inject(StorageService);
  dialog = inject(MatDialog);
  loadingService = inject(LoadingService);

  // Holds the raw search text while the user types, and the PatientDto once an option is picked.
  patientCtrl = new FormControl<PatientDto | string | null>(null);
  filteredPatients: Signal<PatientDto[]>;
  searching = signal(false);

  // Renders the closed field, which keeps showing the selected patient whatever the results are.
  displayPatient = (patientDto: PatientDto | string | null): string => {
    return (patientDto && typeof patientDto !== 'string') ? patientDto.firstName + ' ' + patientDto.lastName : '';
  };

  private selectedPatientDto?: PatientDto;

  constructor() {
    const storedPatientDto = this.storageService.loadPatient();
    if (storedPatientDto) {
      this.selectedPatientDto = storedPatientDto;
      this.patientCtrl.setValue(storedPatientDto);
    }

    const searchChanges = this.patientCtrl.valueChanges.pipe(
      startWith(''),
      // Before the map, so that going from a patient back to an empty field still counts as a
      // change: both map to '' and would otherwise cancel each other out.
      distinctUntilChanged(),
      // Picking an option emits the patient itself, which is not a search.
      map(value => typeof value === 'string' ? value : ''),
      tap(searchText => this.searching.set(searchText.length >= minSearchTextLength)),
      debounceTime(300));

    this.filteredPatients = toSignal(searchChanges.pipe(
      switchMap(searchText => {
        if (searchText.length < minSearchTextLength) {
          return of<PatientDto[]>(this.selectedPatientDto ? [this.selectedPatientDto] : []);
        }

        this.loadingService.skipIndicator = true;

        const patientSearchDto: PatientSearchDto = {
          searchText: searchText,
          customerId: this.memoryService.customerId ?? 0,
        };

        // A failed search must not reach the source observable: toSignal would rethrow the error on
        // every read, so the template would throw on each change detection and take the page down.
        return this.appointmentSelectionService.findPatient(patientSearchDto).pipe(
          catchError(() => of<PatientDto[]>([])));
      }),
      delay(200),
      tap(() => this.searching.set(false))), {initialValue: []});
  }

  ngOnInit() {
    this.appointmentSelectionService.init();
  }

  addPatient() {
    this.dialog.open(CreatePatientDialog, {autoFocus: false}).afterClosed()
      .subscribe((patientDto?: PatientDto) => {
        // Cancelling closes without a patient.
        if (patientDto) {
          this.selectPatient(patientDto);
        }
      });
  }

  patientSelected(event: MatAutocompleteSelectedEvent) {
    this.selectPatient(event.option.value);
  }

  private selectPatient(patientDto: PatientDto) {
    const patientChanged = this.selectedPatientDto?.patientId !== patientDto.patientId;

    this.selectedPatientDto = patientDto;
    this.patientCtrl.setValue(patientDto);
    this.memoryService.patientId = patientDto.patientId;
    this.storageService.storePatient(patientDto);

    // The time slots already loaded were queried for the previous patient.
    if (patientChanged) {
      this.appointmentSelectionService.reloadTimeSlotDtos();
    }
  }

  // Drops a half-typed search that was never turned into a selection. Bound to the blur of the
  // input as well, because the autocomplete only emits (closed) for a panel that was open and a
  // search without results never opens one.
  restoreSelection() {
    if (typeof this.patientCtrl.value === 'string') {
      this.patientCtrl.setValue(this.selectedPatientDto ?? null);
    }
  }
}
